import { toSignificant, toFactor } from '@/utilities/formatting';
import { SWS_ANALYSIS_MODEL, SWS_HOST } from '@/constants';
import DataTable from '@/components/DataTable';

export interface ValuationPERatioProps {
  uniqueSymbol: string;
  lastEarningsUpdate: number;
  reportingCurrencyISO: string;
  peValue?: number;
  reportingCurrencySymbol: string;
  earningsPerShare: number;
  exchangeSymbol: string;
  tickerSymbol: string;
  latestPriceDate: number;
  primaryTradingCurrencySymbol: string;
  primaryTradingCurrencyISO: string;
  primaryPrice: number;
  primaryCurrencyReported: number;
  isExchangeRateNeeded: boolean;
  exchangeRate?: number;
  industryCountryName: string;
  industrySecondaryName: string;
  industryAveragePECount: number;
  industryAveragePE: number;
  marketCountryName: string;
  marketAveragePECount: number;
  marketAveragePE: number;
  companyShortName: string;
}

const ValuationPERatio = ({
  uniqueSymbol,
  lastEarningsUpdate,
  reportingCurrencyISO,
  peValue,
  reportingCurrencySymbol,
  earningsPerShare,
  exchangeSymbol,
  tickerSymbol,
  latestPriceDate,
  primaryTradingCurrencyISO,
  primaryPrice,
  primaryTradingCurrencySymbol,
  primaryCurrencyReported,
  isExchangeRateNeeded,
  exchangeRate,
  industryCountryName,
  industrySecondaryName,
  industryAveragePECount,
  industryAveragePE,
  marketCountryName,
  marketAveragePECount,
  marketAveragePE,
  companyShortName,
}: ValuationPERatioProps) => {
  return (
    <div>
      <DataTable columnWidths="4, 4, 2" light>
        <caption>{`${uniqueSymbol} PE (Price to Earnings) Ratio Data Sources`}</caption>
        <thead>
          <tr>
            <th>Data Point</th>
            <th>Source</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <span>Earnings Per Share</span>
              <sup>*</sup>
            </td>
            <td>{`Company Filings (${new Date(
              lastEarningsUpdate
            ).toLocaleDateString()}) in ${reportingCurrencyISO}`}</td>
            <td>
              {peValue
                ? `${reportingCurrencySymbol}${toSignificant(
                    earningsPerShare,
                    2
                  )}`
                : 'Not available'}
            </td>
          </tr>
          <tr>
            <td>
              <span>{`${exchangeSymbol}:${tickerSymbol} Share Price`}</span>
              <sup>**</sup>
            </td>
            <td>{`${exchangeSymbol} (${new Date(
              latestPriceDate
            ).toLocaleDateString()}) in ${primaryTradingCurrencyISO}`}</td>
            <td>{`${primaryTradingCurrencySymbol}${toSignificant(
              primaryPrice,
              2
            )}`}</td>
          </tr>
          {isExchangeRateNeeded && (
            <tr>
              <td>
                {`${exchangeSymbol}:${tickerSymbol} Share Price converted to ${reportingCurrencyISO} reporting currency`}
              </td>
              <td>
                {`Exchange rate (${primaryTradingCurrencyISO}/${reportingCurrencyISO}) ${toSignificant(
                  exchangeRate ? exchangeRate : 0,
                  3
                )}`}
              </td>
              <td>
                {`${reportingCurrencySymbol}${toSignificant(
                  primaryCurrencyReported,
                  2
                )}`}
              </td>
            </tr>
          )}
          {industryAveragePE && (
            <tr>
              <td>
                {`${industryCountryName} ${industrySecondaryName} Industry PE Ratio`}
              </td>
              <td>
                {`Median Figure of ${toSignificant(
                  industryAveragePECount
                )} Publicly-Listed ${industrySecondaryName} Companies`}
              </td>
              <td>{toFactor(industryAveragePE, 2)}</td>
            </tr>
          )}
          {marketAveragePE && (
            <tr>
              <td>{`${marketCountryName} Market PE Ratio`}</td>
              <td>
                {`Median Figure of ${toSignificant(
                  marketAveragePECount
                )} Publicly-Listed Companies`}
              </td>
              <td>{toFactor(marketAveragePE, 2)}</td>
            </tr>
          )}
        </tbody>
      </DataTable>
      <p>
        <sup>*</sup>
        <a
          href={`${SWS_HOST}/hc/en-us/articles/115006170908-Why-is-some-of-the-data-on-Simply-Wall-St-different-to-other-sites`}
          data-unique-symbol={uniqueSymbol}
          data-about-section="valuation_pe_ratio"
          data-link-name="zendesk_ttm"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Trailing twelve months (TTM)
        </a>
        <span>
          {` annual GAAP earnings per share excluding extraordinary items.`}
        </span>
      </p>
      <p>
        <sup>**</sup>
        <span>{`Primary Listing of ${companyShortName}`}</span>
      </p>
      {peValue && (
        <DataTable columnWidths="4, 4, 2" light>
          <caption>
            {`${uniqueSymbol} PE (Price to Earnings) Ratio Calculation`}
          </caption>
          <thead>
            <tr>
              <th />
              <th>Calculation</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>PE Ratio</td>
              <td>
                <span>{`= ${exchangeSymbol}:${tickerSymbol} Share Price ÷ EPS (both in ${reportingCurrencyISO})`}</span>
                <br />
                <span>{`${toSignificant(
                  primaryCurrencyReported,
                  2
                )} ÷ ${toSignificant(earningsPerShare, 2)}`}</span>
              </td>
              <td>{toFactor(peValue, 2)}</td>
            </tr>
          </tbody>
        </DataTable>
      )}
      <p>
        <span>{`Learn more about our ratios and growth rates in Simply Wall St’s `}</span>
        <a
          href={`${SWS_ANALYSIS_MODEL}/MODEL.markdown`}
          data-unique-symbol={uniqueSymbol}
          data-about-section="valuation_pe_ratio"
          data-link-name="model_growth_rates"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          analysis model.
        </a>
      </p>
    </div>
  );
};

export default ValuationPERatio;
